<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { faArrowLeft, faArrowRight } from '@fortawesome/duotone-regular-svg-icons'
import { useAppColor } from '~/composables/useAppColor'
import Button from '~/components/ui/Button.vue'

const { nav, itemCount } = defineProps<{
  nav: 'below' | 'floating'
  itemCount: number
}>()

const { t } = useI18n()
const { themeColor } = useAppColor()

const index = ref(0)

const navClasses = computed(() => ({
  'flex justify-center items-center pt-8': nav === 'below',
  'absolute bottom-1/2 translate-y-1/2 left-0 right-0 flex justify-between items-center gap-6 transition-opacity': nav === 'floating',
}))

function scrollBy(n: number) {
  index.value = Math.max(0, Math.min(itemCount - 1, index.value + n))
}
</script>

<template>
  <div
    class="relative grid grid-rows-1 focus-visible:ring"
    tabindex="0"
    @keydown.left.prevent="scrollBy(-1)"
    @keydown.right.prevent="scrollBy(1)"
  >
    <div class="relative aspect-video size-full">
      <slot :index="index" />
    </div>

    <nav
      v-if="itemCount > 1"
      class="pointer-events-none z-2 transition-[opacity,visibility]"
      :class="navClasses"
    >
      <Button
        data-name="carousel-nav-left"
        class="carousel-nav-button pointer-events-auto transition-[opacity,visibility]"
        :class="{ 'pointer-events-none invisible opacity-0': index === 0 }"
        :variant="nav === 'below' ? 'outline' : 'solid'"
        shape="square"
        size="xl"
        :color="themeColor ?? 'white'"
        :title="t('previous')"
        :aria-label="t('previous')"
        @click="scrollBy(-1)"
      >
        <font-awesome-icon :icon="faArrowLeft" />
      </Button>

      <Button
        data-name="carousel-nav-right"
        class="carousel-nav-button pointer-events-auto transition-[opacity,visibility]"
        :class="{ 'pointer-events-none invisible opacity-0': index + 1 === itemCount }"
        :variant="nav === 'below' ? 'outline' : 'solid'"
        shape="square"
        size="xl"
        :color="themeColor ?? 'white'"
        :title="t('next')"
        :aria-label="t('next')"
        @click="scrollBy(1)"
      >
        <font-awesome-icon :icon="faArrowRight" />
      </Button>
    </nav>
  </div>
</template>
